/**
 * LifeScribe Admin Dashboard - Icon Mapping System
 * Standardized Material Icons mapping for consistent iconography
 * Based on LifeScribe Design System v4.0
 */

export const icons = {
  // ===== NAVIGATION =====
  dashboard: 'dashboard',
  overview: 'view_quilt',
  users: 'people',
  revenue: 'account_balance',
  subscriptions: 'card_membership',
  content: 'article',
  ai: 'psychology',
  communications: 'mail',
  family: 'family_restroom',
  monitoring: 'insights',
  cost: 'attach_money',
  system: 'settings_applications',
  profile: 'account_circle',
  logout: 'logout',

  // ===== ACTIONS =====
  add: 'add_circle',
  addAlt: 'add',
  edit: 'edit',
  delete: 'delete',
  save: 'save',
  cancel: 'close',
  search: 'search',
  filter: 'filter_list',
  export: 'download',
  import: 'upload',
  refresh: 'refresh',
  sync: 'sync',
  copy: 'content_copy',
  share: 'share',
  more: 'more_vert',
  menu: 'menu',
  back: 'arrow_back',
  forward: 'arrow_forward',
  expand: 'expand_more',
  collapse: 'expand_less',
  close: 'close',
  open: 'open_in_new',

  // ===== STATUS & INDICATORS =====
  success: 'check_circle',
  warning: 'warning',
  error: 'error',
  info: 'info',
  help: 'help',
  pending: 'schedule',
  active: 'check_circle',
  inactive: 'cancel',
  verified: 'verified',
  unverified: 'unpublished',
  locked: 'lock',
  unlocked: 'lock_open',
  visibility: 'visibility',
  visibilityOff: 'visibility_off',
  star: 'star',
  starOutline: 'star_outline',
  favorite: 'favorite',
  favoriteOutline: 'favorite_border',

  // ===== METRICS & ANALYTICS =====
  trending_up: 'trending_up',
  trending_down: 'trending_down',
  trending_flat: 'trending_flat',
  growth: 'show_chart',
  analytics: 'analytics',
  pie_chart: 'pie_chart',
  bar_chart: 'bar_chart',
  line_chart: 'show_chart',
  table: 'table_chart',

  // ===== USER MANAGEMENT =====
  user: 'person',
  userAdd: 'person_add',
  userRemove: 'person_remove',
  userGroup: 'group',
  admin: 'admin_panel_settings',
  badge: 'badge',
  email: 'email',
  phone: 'phone',
  location: 'location_on',

  // ===== REVENUE & PAYMENTS =====
  payment: 'payment',
  credit_card: 'credit_card',
  money: 'attach_money',
  wallet: 'account_balance_wallet',
  receipt: 'receipt',
  invoice: 'description',
  discount: 'local_offer',
  refund: 'replay',

  // ===== CONTENT & MEDIA =====
  image: 'image',
  video: 'videocam',
  audio: 'audiotrack',
  document: 'description',
  folder: 'folder',
  file: 'insert_drive_file',
  cloud: 'cloud',
  upload_file: 'upload_file',
  download_file: 'download',
  attach: 'attach_file',

  // ===== COMMUNICATIONS =====
  message: 'message',
  chat: 'chat',
  notification: 'notifications',
  bell: 'notifications_active',
  inbox: 'inbox',
  send: 'send',
  campaign: 'campaign',
  broadcast: 'campaign',

  // ===== FAMILY & COLLABORATION =====
  family_group: 'family_restroom',
  invite: 'person_add_alt',
  collaborate: 'groups',
  vault: 'folder_shared',
  legacy: 'history_edu',

  // ===== MONITORING & SYSTEM =====
  health: 'favorite',
  cpu: 'developer_board',
  memory: 'memory',
  storage: 'storage',
  database: 'database',
  api: 'api',
  code: 'code',
  bug: 'bug_report',
  speed: 'speed',
  performance: 'speed',
  logs: 'article',
  audit: 'fact_check',

  // ===== AI & AUTOMATION =====
  ai_bot: 'psychology',
  automation: 'auto_fix_high',
  magic: 'auto_awesome',
  brain: 'psychology',
  creative: 'brush',
  generate: 'auto_awesome',
  optimize: 'tune',

  // ===== SETTINGS & CONFIG =====
  settings: 'settings',
  config: 'tune',
  toggle_on: 'toggle_on',
  toggle_off: 'toggle_off',
  security: 'security',
  privacy: 'privacy_tip',
  key: 'vpn_key',

  // ===== TIME & CALENDAR =====
  calendar: 'calendar_today',
  event: 'event',
  schedule: 'schedule',
  clock: 'access_time',
  timer: 'timer',
  history: 'history',
  today: 'today',

  // ===== EMPTY STATES & PLACEHOLDERS =====
  empty_box: 'inbox',
  no_data: 'insert_chart_outlined',
  no_results: 'search_off',
  no_content: 'article',
  placeholder: 'crop_square',

  // ===== SPECIAL =====
  lifescribe_logo: 'auto_stories',
  premium: 'workspace_premium',
  flag: 'flag',
  bookmark: 'bookmark',
  bookmarkOutline: 'bookmark_border',
  link: 'link',
  external_link: 'open_in_new',
} as const;

/**
 * Type-safe icon name
 */
export type IconName = keyof typeof icons;

/**
 * Get Material Icon name for a semantic icon name
 * @param name - Semantic icon name
 * @returns Material Icon name
 */
export const getIcon = (name: IconName): string => {
  return icons[name];
};

/**
 * Screen-specific icon mappings
 */
export const screenIcons = {
  dashboard: icons.dashboard,
  users: icons.users,
  revenue: icons.revenue,
  subscriptions: icons.subscriptions,
  content: icons.content,
  ai: icons.ai,
  communications: icons.communications,
  family: icons.family_group,
  monitoring: icons.monitoring,
  cost: icons.cost,
  system: icons.system,
  brainHealth: icons.brain,
} as const;

/**
 * Status icon mappings
 */
export const statusIcons = {
  success: icons.success,
  warning: icons.warning,
  error: icons.error,
  info: icons.info,
  active: icons.active,
  inactive: icons.inactive,
  pending: icons.pending,
  verified: icons.verified,
  unverified: icons.unverified,
} as const;

/**
 * Action icon mappings
 */
export const actionIcons = {
  add: icons.add,
  edit: icons.edit,
  delete: icons.delete,
  save: icons.save,
  cancel: icons.cancel,
  search: icons.search,
  filter: icons.filter,
  export: icons.export,
  import: icons.import,
  refresh: icons.refresh,
  more: icons.more,
} as const;

/**
 * Metric trend icon mappings
 */
export const trendIcons = {
  up: icons.trending_up,
  down: icons.trending_down,
  flat: icons.trending_flat,
} as const;
